import {
    RelatedPartiesDto,
    RelatedPartyRowDto,
    RelatedPartyRowUpdateDto,
    RepresentationGrounds,
} from 'types/clientIdentification';
import { PartyType } from 'types/contacts';

export function toRowUpdate(row: RelatedPartyRowDto): RelatedPartyRowUpdateDto {
    return {
        id: row.id,
        roleCode: row.roleCode,
        linkedPartyId: row.linkedPartyId,
        name: row.name,
        note: row.note,
        actingRoleCode: row.actingRoleCode,
        representationGroundsCode: row.representationGroundsCode,
        representationGroundsNote: row.representationGroundsNote,
    };
}

/** Every stored row, the ones in a role the tier no longer asks included: the PUT replaces the whole list. */
export function allRowUpdates(relatedParties: RelatedPartiesDto): RelatedPartyRowUpdateDto[] {
    return [
        ...relatedParties.groups.flatMap((group) => group.rows),
        ...relatedParties.notAskedAtTier.flatMap((role) => role.rows),
    ].map(toRowUpdate);
}

/**
 * The given rows in a role taking grounds: a person who also holds a role that does not represent the client answers
 * the grounds for their representing roles only, and the backend refuses grounds on any other row.
 */
export function rowIdsTakingGrounds(relatedParties: RelatedPartiesDto, rowIds: number[]): number[] {
    return relatedParties.groups
        .filter((group) => group.takesGrounds)
        .flatMap((group) => group.rows)
        .map((row) => row.id)
        .filter((id) => rowIds.includes(id));
}

/** The rows with the grounds answered on the given ones; the note is kept only with "Other". */
export function withGrounds(
    rows: RelatedPartyRowUpdateDto[],
    rowIds: number[],
    code: RepresentationGrounds | null,
    note: string | null
): RelatedPartyRowUpdateDto[] {
    return rows.map((row) =>
        row.id !== undefined && rowIds.includes(row.id)
            ? { ...row, representationGroundsCode: code, representationGroundsNote: code === 'OTHER' ? note : null }
            : row
    );
}

export interface DraftRow extends RelatedPartyRowUpdateDto {
    key: string;
    linkedPartyName: string | null;
    linkedPartyType: PartyType | null;
}

export function toDraftRow(row: RelatedPartyRowDto): DraftRow {
    return {
        ...toRowUpdate(row),
        key: `row-${row.id}`,
        linkedPartyName: row.linkedPartyName,
        linkedPartyType: row.linkedPartyType,
    };
}

export function draftToUpdate({ key: _key, linkedPartyName: _name, linkedPartyType: _type, ...row }: DraftRow) {
    return row;
}

export function newDraftRow(key: string, roleCode: string, entry: { contact?: MinContact; name?: string }): DraftRow {
    return {
        key,
        roleCode,
        linkedPartyId: entry.contact?.id ?? null,
        linkedPartyName: entry.contact?.name ?? null,
        linkedPartyType: entry.contact?.partyType ?? null,
        name: entry.contact ? null : (entry.name ?? null),
        note: null,
        actingRoleCode: null,
        representationGroundsCode: null,
        representationGroundsNote: null,
    };
}

export interface MinContact {
    id: number;
    name: string;
    partyType: PartyType;
}
